// @flow
import update from 'immutability-helper';
import { ofType, StateObservable } from 'redux-observable';
import { catchError, map, mergeMap, takeUntil } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { getPopularProductsApi } from './api';
import { debounceTime } from 'rxjs/operators';
import Paginator from '../../common/Paginator';
import { encodeProduct } from '../featured-products/utils/encode';

import type { HTTPError } from '../../error';
import type { RootState } from '../index';
import type { Product } from '../featured-products/duck';

export type ProductsFilter = {
  categories?: string[];
  minPrice?: number;
  maxPrice?: number;
  stores?: string[];
  freeShipping?: boolean;
  freeTax?: boolean;
};

type PopularProductsAction = {
  +type: string;
  +products?: Product[];
  +filters?: ProductsFilter;
  +error?: HTTPError;
  +resetPagination?: boolean;
};

export type PopularProductsState = {
  isFetchingProducts?: boolean;
  fetchProductsError?: HTTPError;
  products?: Product[];
  page?: number;
  pageCanAsk?: boolean;
};

const defaultDebounceTime = 800;

// Actions
const POPULAR_PRODUCT_FETCH_REQUEST = 'POPULAR_PRODUCT_FETCH_REQUEST';
const POPULAR_PRODUCT_FETCH_SUCCESS = 'POPULAR_PRODUCT_FETCH_SUCCESS';
const POPULAR_PRODUCT_FETCH_STATE = 'POPULAR_PRODUCT_FETCH_STATE';
const POPULAR_PRODUCT_RESET = 'POPULAR_PRODUCT_RESET';

export function fetchPopularProducts(filters: ProductsFilter, resetPagination?: boolean): PopularProductsAction {
  return {
    type: POPULAR_PRODUCT_FETCH_REQUEST,
    filters,
    resetPagination,
  }
}

export function fetchPopularProductsSuccess(products: Product[]): PopularProductsAction {
  return {
    type: POPULAR_PRODUCT_FETCH_SUCCESS,
    products,
  }
}

export function fetchPopularProductsError(error: HTTPError): PopularProductsAction {
  return {
    type: POPULAR_PRODUCT_FETCH_STATE,
    error,
  }
}

export function resetState(): PopularProductsAction {
  return {
    type: POPULAR_PRODUCT_RESET,
  }
}

export const actions = {
  POPULAR_PRODUCT_FETCH_REQUEST,
  POPULAR_PRODUCT_FETCH_SUCCESS,
  POPULAR_PRODUCT_FETCH_STATE,
  POPULAR_PRODUCT_RESET,
  fetchPopularProducts,
  fetchPopularProductsSuccess,
  fetchPopularProductsError,
  resetState,
};

export const initialState: PopularProductsState = {};

const paginator = new Paginator(['popularProducts']);

export default function reducer(state: PopularProductsState = initialState,
                                action: PopularProductsAction): PopularProductsState {
  switch (action.type) {
    case POPULAR_PRODUCT_FETCH_REQUEST:
      if (action.resetPagination) {
        paginator.reset('popularProducts');
        return update(state, {
          isFetchingProducts: { $set: true },
          products: { $set: paginator.getItems('popularProducts') },
          page: { $set: paginator.getPage('popularProducts') },
          pageCanAsk: { $set: paginator.getCanAsk('popularProducts') },
        });
      }
      return {
        ...state,
        isFetchingProducts: true,
      };

    case POPULAR_PRODUCT_FETCH_SUCCESS:
      paginator.increment('popularProducts', action.products, state.products);
      return update(state, {
        products: { $set: paginator.getItems('popularProducts') },
        page: { $set: paginator.getPage('popularProducts') },
        pageCanAsk: { $set: paginator.getCanAsk('popularProducts') },
        isFetchingProducts: { $set: false },
      });
    case POPULAR_PRODUCT_FETCH_STATE:
      return update(state, {
        fetchStoresError: { $set: action.error },
        $unset: ['isFetchingProducts'],
      });
    case POPULAR_PRODUCT_RESET:
      paginator.resetAll();
      return initialState;
    default:
      return state;
  }
}

export function fetchPopularProductsEpic(action$: Observable<PopularProductsAction>,
                                         state$: StateObservable<RootState>): Observable<PopularProductsAction> {
  return action$.pipe(
    ofType(POPULAR_PRODUCT_FETCH_REQUEST),
    debounceTime(defaultDebounceTime),
    mergeMap(({ filters }) => {
      return getPopularProductsApi({
        ...filters,
        page: state$.value.popularProducts.page || paginator.getPage('popularProducts'),
        limit: paginator.getLimit(),
      }).pipe(
        takeUntil(action$.pipe(ofType(POPULAR_PRODUCT_RESET))),
        takeUntil(action$.pipe(ofType(POPULAR_PRODUCT_FETCH_REQUEST))),
        map((response: Product[]) => {
          const newProducts = response.map((p: Product, index: number) => ({
            ...p,
            id: `${(state$.value.popularProducts.page * paginator.getLimit()) + index}`,
            encoded: encodeProduct(p),
          }));
          return fetchPopularProductsSuccess(newProducts);
        }),
        catchError((e: HTTPError) => of(fetchPopularProductsError(e))))
    }));
}



// WEBPACK FOOTER //
// ./src/modules/popular-products/duck.js